/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const LoginForm = ({ onBackToHome, onSuccessfulLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    studentId: '',
    pin: ''
  });
  const [error, setError] = useState('');
  const [staffRedirect, setStaffRedirect] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(null);
  const [showTokenEntry, setShowTokenEntry] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    setStaffRedirect(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',  // Add this line
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Different messages based on lockout type
        if (data.type === 'staff_login_required') {
          setStaffRedirect(data.message);
          return;
        } else if (data.type === 'ip_rate_limit') {
          throw new Error('Too many login attempts from your network. Please try again in 15 minutes.');
        } else if (data.type === 'account_locked') {
          throw new Error(data.message || 'Account locked. Please try again later.');
        } else if (data.type === 'invalid_credentials') {
          throw new Error(data.message || 'Invalid credentials.');
        } else {
          throw new Error(data.error || data.message || 'Login failed');
        }
      }
      console.log('Login successful:', data);
      onSuccessfulLogin({
        ...data,
        firstName: data.firstName || data.first_name
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinReset = async (e) => {
    e.preventDefault();
    if (!formData.studentId) {
      setError('Please enter your Student ID first');
      return;
    }

    setError('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/request-pin-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ studentId: formData.studentId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'PIN reset request failed');
      }

      // Show success message and switch to token entry mode
      setResetSuccess(`Reset code sent to ${data.email.replace(/(.{3}).+@/, '$1***@')}. Check your email and enter the code below.`);
      setShowResetForm(false);
      setShowTokenEntry(true);
      setError('');
    } catch (err) {
      setError(err.message);
      // Close the reset form dialog on error so user sees the error message clearly
      setShowResetForm(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyToken = async (e) => {
    e.preventDefault();
    if (!resetToken || resetToken.length !== 6) {
      setError('Please enter the 6-digit code from your email');
      return;
    }

    setError('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify-reset-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          studentId: formData.studentId,
          token: resetToken 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Token verification failed');
      }

      // Success! Show the new PIN
      setResetSuccess(null);
      setShowTokenEntry(false);
      setResetToken('');
      setResetSuccess(data.newPin);
      
      // Clear form for new login
      setFormData({ ...formData, pin: '' });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card className="w-full max-w-md mx-auto m-2 sm:m-4">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-2xl">Login</CardTitle>
          <CardDescription className="text-sm sm:text-base">Enter your credentials to access your account</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="studentId">Student ID</Label>
              <Input
                id="studentId"
                name="studentId"
                type="text"
                required
                value={formData.studentId}
                onChange={handleInputChange} disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pin">PIN</Label>
              <Input
                id="pin"
                name="pin"
                type="password"
                required
                value={formData.pin}
                onChange={handleInputChange} disabled={isLoading}
              />
            </div>
            {staffRedirect && (
              <Alert className="border-blue-200 bg-blue-50">
                <AlertDescription className="text-blue-900">
                  <p className="mb-2">{staffRedirect}</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => navigate('/staff-login')}
                  >
                    Go to Staff Login
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            {error && !staffRedirect && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {resetSuccess && !showTokenEntry && (
              <Alert>
                <AlertDescription>
                  <div className="text-center">
                    {resetSuccess.includes('Reset code sent') ? (
                      <p className="text-sm">{resetSuccess}</p>
                    ) : (
                      <div>
                        <p className="font-semibold">Your new PIN is:</p>
                        <p className="text-2xl font-mono font-bold mt-2 mb-2">{resetSuccess}</p>
                        <p className="text-sm text-muted-foreground">Please write this down and use it to log in.</p>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            {showTokenEntry && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border">
                <div className="text-center">
                  <p className="text-sm font-medium text-blue-900 mb-2">Check Your Email</p>
                  <p className="text-xs text-blue-700">{resetSuccess}</p>
                </div>
                <div>
                  <Label htmlFor="resetToken">Enter 6-digit code</Label>
                  <Input
                    id="resetToken"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="text-center text-lg font-mono"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setShowTokenEntry(false);
                      setResetSuccess(null);
                      setResetToken('');
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleVerifyToken}
                    disabled={isLoading || resetToken.length !== 6}
                    className="flex-1"
                  >
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                </div>
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
            
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  if (!formData.studentId.trim()) {
                    setError('Please enter your Student ID first, then click "Forgot your PIN?"');
                    return;
                  }
                  setError('');
                  setShowResetForm(true);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                disabled={isLoading}
              >
                Forgot your PIN?
              </button>
            </div>
            
            <Button type="button" variant="outline" className="w-full" onClick={onBackToHome} disabled={isLoading}>
              Back to Home
            </Button>

            <div className="text-center pt-4 border-t mt-4">
              <button
                type="button"
                onClick={() => navigate('/staff-login')}
                className="text-xs text-gray-500 hover:text-gray-700"
                disabled={isLoading}
              >
                Staff Login →
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* PIN Reset Confirmation Dialog */}
      <Dialog open={showResetForm} onOpenChange={setShowResetForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Your PIN</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Are you sure you want to reset your PIN?</p>
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800">
                <strong>Student ID:</strong> {formData.studentId || 'Please enter your Student ID first'}
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              A new PIN will be generated for you. Make sure to write it down!
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetForm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePinReset} 
              disabled={!formData.studentId || isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginForm;